import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { extractErrorMessage } from '../utils/errorHandling';
import { verifyOAuthState } from '../utils/oauth';
import { AuthResponse } from '../api/auth';
import { AxiosResponse } from 'axios';

/**
 * Custom hook to handle OAuth callback flow for all providers
 *
 * This hook eliminates code duplication across GoogleCallback, AppleCallback, and GitHubCallback components.
 * It handles the entire OAuth callback flow:
 * 1. State verification (CSRF protection)
 * 2. Error handling from OAuth provider
 * 3. Authorization code exchange
 * 4. Token storage and auth context update
 * 5. New user welcome modal detection
 * 6. Navigation to dashboard
 *
 * @param provider - OAuth provider name ('google', 'apple', 'github')
 * @param callbackFn - Provider-specific callback function to exchange code for tokens
 * @returns Object with loading state, error state, welcome modal state, and user info
 */
export const useOAuthCallback = (
  provider: 'google' | 'apple' | 'github',
  callbackFn: (params: { code: string; state: string; redirect_uri?: string }) => Promise<AxiosResponse<AuthResponse>>
) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleOAuthCallback } = useAuth();
  const { addError } = useError();
  const [showWelcome, setShowWelcome] = useState(false);
  const [userInfo, setUserInfo] = useState<{ displayName: string; email: string } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Quick client-side validation for better UX (real CSRF protection happens server-side)
      if (!verifyOAuthState(provider, state || '')) {
        addError('OAuth session expired or invalid. Please try signing in again.');
        navigate('/login');
        return;
      }

      // Handle OAuth errors from provider
      if (error) {
        console.error(`${provider} OAuth error:`, error, errorDescription);
        const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);
        addError(errorDescription || `${providerName} authentication failed. Please try again.`);
        navigate('/login');
        return;
      }

      // Missing authorization code
      if (!code) {
        addError('Missing authorization code. Please try again.');
        navigate('/login');
        return;
      }

      try {
        // Exchange authorization code for tokens via backend (includes CSRF state validation)
        const response = await callbackFn({ code, state: state || '' });

        // Check cancellation only after async operation (protects against user navigating away during API call)
        if (cancelled) return;

        // Update auth state via AuthContext (stores tokens and sets user)
        await handleOAuthCallback(response.data);

        // Show welcome modal for new users
        if (response.data.is_new_user) {
          setUserInfo({
            displayName: response.data.user.display_name,
            email: response.data.user.email,
          });
          setShowWelcome(true);
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        if (cancelled) return;

        console.error(`${provider} callback error:`, error);
        const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);
        const apiError = extractErrorMessage(error, `${providerName} authentication failed. Please try again.`);
        addError(apiError.message);
        navigate('/login');
      }
    };

    handleCallback();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, handleOAuthCallback, addError, provider, callbackFn]);

  const handleWelcomeClose = () => {
    setShowWelcome(false);
    navigate('/dashboard');
  };

  return {
    showWelcome,
    userInfo,
    handleWelcomeClose,
  };
};
